import { ApplicationRef, ErrorHandler, Injector, NgModule, provideZonelessChangeDetection } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { UIRouterModule } from '@uirouter/angular';
import { UIRouter } from '@uirouter/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {ADN_TRANSLATIONS} from "./common/translations";
import {FormsModule} from "@angular/forms";
import { QuillModule } from 'ngx-quill';
import { ToastrModule } from 'ngx-toastr';

import { TranslateModule, TranslateParser, TranslateService } from '@ngx-translate/core';
import { AdnTranslateParser } from './common/filters/adn-translate-parser';
import { installMissingTranslateKeyReporting } from './common/filters/missing-translate-key';
import { AdnLogService } from '../components/util/adn-log.service';

import {PaymentModule} from "./payment/payment.module";
import {CouponModule} from "./admin/coupon/coupon.module";
import {BlocklistModule} from "./blocklist/blocklist.module";
import {LineItemLabelsModule} from "./line-item/line-item-labels.module";
import {WorkspaceWidgetsModule} from "./workspace/workspace-widgets.module";
import {NotesModule} from "./common/directives/notes/notes.module";
import {ObjectStatesModule} from "./common/directives/object-states.module";
import {ResourceStatusModule} from "./common/directives/resource-status.module";
import {CodemirrorModule} from "./common/directives/codemirror.module";
import {AppSharedModule} from "./common/app-shared.module";
import {AdnSelectModule} from "./common/directives/adn-select/adn-select.module";
import {FormControlsModule} from "./common/directives/form-controls/form-controls.module";
import {AdminListModule} from "./common/directives/admin-list.module";
import {CountrySelectModule} from "./common/directives/country-select/country-select.module";
import {ChartsModule} from "./common/directives/charts.module";
import {StatsTablesModule} from "./common/controller/stats-tables.module";
import {ListColumnSelectorsModule} from "./common/controller/list-column-selectors.module";
import {AdminResourcePagesModule} from "./common/controller/admin-resource-pages.module";
import {SearchSelectModule} from "./common/controller/search-select.module";
import {DeliveryWidgetsModule} from "./common/controller/delivery-widgets.module";
import {CreativePreviewModule} from "./creative/creative-preview.module";
import {CreativeAssetsModule} from "./creative/creative-assets.module";
import {CustomEventModule} from "./admin/custom-event/custom-event.module";
import {ApiKeyModule} from "./admin/api-key/api-key.module";
import {RulesetModule} from "./ruleset/ruleset.module";
import {ExplorationModule} from "./common/directives/exploration.module";
import {PublisherStatsModule} from "./common/controller/publisher-stats.module";
import {CdnUploadModule} from "./admin/cdn-upload/cdn-upload.module";
import {RoleModule} from "./admin/role/role.module";
import {EmailTemplateModule} from "./admin/email-template/email-template.module";
import {EmailTranslationModule} from "./admin/email-translation/email-translation.module";
import {InvoiceTranslationModule} from "./invoice-translation/invoice-translation.module";
import {ReportTranslationModule} from "./report-translation/report-translation.module";
import {TierModule} from "./admin/tier/tier.module";
import {LayoutIncludeModule} from "./admin/layout-include/layout-include.module";
import {SiteGroupModule} from "./site-group/site-group.module";
import {TeamGroupModule} from "./admin/team-group/team-group.module";
import {DataExportModule} from "./admin/data-export/data-export.module";
import {ReportScheduleModule} from "./report-schedule/report-schedule.module";
import {ReportWidgetsModule} from "./report/report-widgets.module";
import {TargetingModule} from "./targeting/targeting.module";
import {CreativeUserStatesComponent} from "./common/directives/creative-user-states";
import {ReportRoutePageComponent} from "./report/report-route-page.component";
import {ReportListPageComponent} from "./report/report-list-page.component";
import { localUserProfileTokenProvider, localNetworkProfileTokenProvider } from "./app-compatibility-providers";
import { adUnitResourceProvider } from '../components/api/resources/resource/adunit.resource';
import { adUnitTagResourceProvider } from '../components/api/resources/resource/adunittag.resource';
import { advertiserNoAuthResourceProvider } from '../components/api/resources/resource/advertiser-no-auth.resource';
import { advertiserResourceProvider } from '../components/api/resources/resource/advertiser.resource';
import { apiKeyResourceProvider } from '../components/api/resources/resource/api-key.resource';
import { cdnAssetResourceProvider } from '../components/api/resources/resource/asset/cdn-asset.resource';
import { blocklistResourceProvider } from '../components/api/resources/resource/blocklist.resource';
import { contextServiceConfigurationResourceProvider } from '../components/api/resources/resource/context-service-configuration.resource';
import { couponResourceProvider } from '../components/api/resources/resource/coupon.resource';
import { creativePreviewResourceProvider } from '../components/api/resources/resource/creative-preview.resource';
import { creativeSetInfoResourceProvider, creativeSetResourceProvider } from '../components/api/resources/resource/creative-set.resource';
import { creativeResourceProvider } from '../components/api/resources/resource/creative.resource';
import { customEventResourceProvider } from '../components/api/resources/resource/custom-event.resource';
import { dataExportResourceProvider } from '../components/api/resources/resource/data-export.resource';
import { earningsAccountResourceProvider } from '../components/api/resources/resource/earnings-account.resource';
import { electionNoAuthResourceProvider } from '../components/api/resources/resource/election-no-auth.resource';
import { electionResourceProvider } from '../components/api/resources/resource/election.resource';
import { emailTemplateResourceProvider } from '../components/api/resources/resource/email-template.resource';
import { emailTranslationResourceProvider, emailTranslationTestResourceProvider } from '../components/api/resources/resource/email-translation.resource';
import { diagnosticRequestExecutionResourceProvider } from '../components/api/resources/resource/execution-resources/diagnostic-request-execution.resource';
import { adnExecutionResourceProvider } from '../components/api/resources/resource/execution-resources/execution-resource.resource';
import { reportExecutionResourceProvider } from '../components/api/resources/resource/execution-resources/report-execution.resource';
import { externalDemandSourceResourceProvider } from '../components/api/resources/resource/external-demand-source.resource';
import { externalAdUnitResourceProvider } from '../components/api/resources/resource/externaladunit.resource';
import { fieldMappingResourceProvider } from '../components/api/resources/resource/field-mapping.resource';
import { folderResourceProvider } from '../components/api/resources/resource/folder.resource';
import { folioResourceProvider } from '../components/api/resources/resource/folio.resource';
import { invoiceTranslationPreviewResourceProvider, invoiceTranslationResourceProvider } from '../components/api/resources/resource/invoice-translation.resource';
import { layoutIncludeResourceProvider } from '../components/api/resources/resource/layout-include.resource';
import { layoutResourceProvider } from '../components/api/resources/resource/layout.resource';
import { libraryCreativeResourceProvider } from '../components/api/resources/resource/library-creative.resource';
import { lineItemBookingsResourceProvider, lineItemResourceProvider, lineItemReviewResourceProvider, lineItemTemplateResourceProvider } from '../components/api/resources/resource/line-item.resource';
import { marketplaceProductOptionsResourceProvider, marketplaceProductResourceProvider } from '../components/api/resources/resource/marketplace-product.resource';
import { networkDataResourceProvider } from '../components/api/resources/resource/network-data.resource';
import { networkForecastResourceProvider } from '../components/api/resources/resource/network-forecast.resource';
import { networkProfileResourceProvider } from '../components/api/resources/resource/network-profile.resource';
import { networkInfoResourceProvider, networkResourceProvider } from '../components/api/resources/resource/network.resource';
import { noteResourceProvider } from '../components/api/resources/resource/note.resource';
import { notificationPreferencesResourceProvider } from '../components/api/resources/resource/notification-preference.resource';
import { notificationResourceProvider } from '../components/api/resources/resource/notification.resource';
import { orderResourceProvider } from '../components/api/resources/resource/order.resource';
import { paymentResourceProvider } from '../components/api/resources/resource/payment.resource';
import { politicalAdsResourceProvider } from '../components/api/resources/resource/political-ads.resource';
import { publicPoliticalCampaignResourceProvider } from '../components/api/resources/resource/political-campaign-public.resource';
import { politicalCampaignResourceProvider } from '../components/api/resources/resource/political-campaign.resource';
import { politicalEntityResourceProvider } from '../components/api/resources/resource/political-entity.resource';
import { politicalSponsorResourceProvider } from '../components/api/resources/resource/political-sponsor.resource';
import { previewResourceProvider } from '../components/api/resources/resource/preview.resource';
import { printLineItemResourceProvider } from '../components/api/resources/resource/print-line-item.resource';
import { productFeedFacetsProvider, productFeedResourceProvider, productFeedSearchProvider } from '../components/api/resources/resource/product-feed.resource';
import { productResourceProvider } from '../components/api/resources/resource/product.resource';
import { publisherInfoResourceProvider } from '../components/api/resources/resource/publisher-info.resource';
import { reportComponentResourceProvider } from '../components/api/resources/resource/report-component.resource';
import { reportScheduleResourceProvider } from '../components/api/resources/resource/report-schedule.resource';
import { reportTemplateResourceProvider } from '../components/api/resources/resource/report-template.resource';
import { reportTranslationResourceProvider } from '../components/api/resources/resource/report-translation.resource';
import { roleResourceProvider } from '../components/api/resources/resource/role.resource';
import { rulesetResourceProvider } from '../components/api/resources/resource/ruleset.resource';
import { segmentResourceProvider } from '../components/api/resources/resource/segment.resource';
import { siteGroupResourceProvider } from '../components/api/resources/resource/site-group.resource';
import { consentStatisticResourceProvider, siteCountryResourceProvider, siteResourceProvider } from '../components/api/resources/resource/site.resource';
import { targetingTemplateResourceProvider } from '../components/api/resources/resource/targeting-template.resource';
import { teamGroupResourceProvider } from '../components/api/resources/resource/team-group.resource';
import { teamResourceProvider } from '../components/api/resources/resource/team.resource';
import { thirdPartyAudienceResourceProvider } from '../components/api/resources/resource/third-party-audience.resource';
import { triggerResourceProvider } from '../components/api/resources/resource/trigger.resource';
import { userProfileResourceProvider } from '../components/api/resources/resource/user-profile.resource';
import { userResourceProvider } from '../components/api/resources/resource/user.resource';
import { workspaceResourceProvider } from '../components/api/resources/resource/workspace.resource';
import { adnStatFilterProvider } from './common/filters/adn-stat-filter';
import { angularPlatformProviders } from './angular-platform-providers';
import {PopoverModule} from "ngx-bootstrap/popover";
import {ButtonsModule} from "ngx-bootstrap/buttons";
import {CollapseModule} from "ngx-bootstrap/collapse";
import {ModalModule, MODAL_CONFIG_DEFAULT_OVERRIDE} from "ngx-bootstrap/modal";
import {BsDatepickerModule} from "ngx-bootstrap/datepicker";
import {TimepickerModule} from "ngx-bootstrap/timepicker";
import {TabsModule} from "ngx-bootstrap/tabs";
import {BsDropdownModule} from "ngx-bootstrap/dropdown";
import {AdvertiserListPageComponent} from "./advertiser/advertiser-list-page.component";
import {AdvertiserEditPageComponent} from "./advertiser/advertiser-edit-page.component";
import {AdvertiserInviteEditorModalComponent} from "./advertiser/advertiser-invite-editor-modal.component";
import {AdvertiserInformationPageComponent} from "./advertiser/advertiser-information-page.component";
import {AdvertiserAnalysisPageComponent} from "./advertiser/advertiser-analysis-page.component";
import {AdvertiserItemPageComponent} from "./advertiser/advertiser-item-page.component";
import {StatPipe} from "./common/filters/stat.pipe";
import {DisplayStatPipe} from "./common/filters/display-stat.pipe";
import {CurrencyPipe, DecimalPipe, KeyValuePipe, PercentPipe} from "@angular/common";
import {OgTranslatePipe} from "./common/filters/og-translate.pipe";
import {LocaleDatePipe} from "./common/filters/locale-date.pipe";
import {LocaleNumberPipe} from "./common/filters/locale-number.pipe";
import {LocalePercentPipe} from "./common/filters/locale-percent.pipe";
import {SafeHtmlPipe} from "./common/filters/safe-html.pipe";
import {PercentagePipe} from "./common/filters/percentage.pipe";
import {AdnNumberPipe} from "./common/filters/adn-number.pipe";
import {IsoCurrencyPipe, StringToCurrencyPipe} from "./common/filters/iso-currency.pipe";

import {AdTagGeneratorPageComponent} from "./ad-tag/ad-tag-generator-page.component";
import {AdTagComponent} from "./ad-unit/ad-tag-component";
import {CollapsibleSidebarComponent} from "./common/sidebar/collapsible-sidebar.component";
import {SessionInfoComponent} from "./base/session-info/session-info.component";
import {BaseNewButtonComponent} from "./base/base-new-button/base-new-button.component";
import {ResourcesModule} from "../components/api/resources/resources-module";
import {LayoutComponentSummaryComponent} from "./common/layout-component-summary-component";
import {GuardTypePipe} from "./common/filters/guard-type.pipe";
import {EntityDiagramComponent} from "./common/directives/html/entity-diagram-component";
import {MimeTypeKeyPipe} from "./common/filters/mime-type-key.pipe";
import {FolderEditPageComponent} from "./folder/folder-edit-page.component";
import {FolderAnalysisPageComponent} from "./folder/folder-analysis-page.component";
import {FolderContextRoutePageComponent} from "./folder/folder-context-route-page.component";
import {FolderCopyModalComponent} from "./folder/folder-copy-modal.component";
import {FolderItemPageComponent} from "./folder/folder-item-page.component";
import {FolderListPageComponent} from "./folder/folder-list-page.component";
import {FolderRoutePageComponent} from "./folder/folder-route-page.component";
import {CreativeSetRoutePageComponent} from "./creative-set/creative-set-route-page.component";
import {CreativeSetListPageComponent} from "./creative-set/creative-set-list-page.component";
import {CreativeSetItemPageComponent} from "./creative-set/creative-set-item-page.component";
import {CreativeSetEditPageComponent} from "./creative-set/creative-set-edit-page.component";
import {CreativeSetAnalysisPageComponent} from "./creative-set/creative-set-analysis-page.component";
import {CreativeSetContextRoutePageComponent} from "./creative-set/creative-set-context-route-page.component";
import {CreativeSetContextActionsComponent} from "./creative-set/creative-set-context-actions.component";
import {CreativeSetCopyModalComponent} from "./creative-set/creative-set-copy-modal.component";
import {CreativeSetBulkActionModalComponent} from "./creative-set/creative-set-bulk-action-modal.component";
import {FolioRoutePageComponent} from "./folio/folio-route-page.component";
import {FolioListPageComponent} from "./folio/folio-list-page.component";
import {FolioItemPageComponent} from "./folio/folio-item-page.component";
import {FolioEditPageComponent} from "./folio/folio-edit-page.component";
import {FolioInformationPageComponent} from "./folio/folio-information-page.component";
import {FolioContextRoutePageComponent} from "./folio/folio-context-route-page.component";
import {FolioContextActionsComponent} from "./folio/folio-context-actions.component";
import {FolioListObjectStatesComponent} from "./folio/folio-list-object-states.component";
import {FolioCopyModalComponent} from "./folio/folio-copy-modal.component";
import {WorkspaceRoutePageComponent} from "./workspace/workspace-route-page.component";
import {WorkspaceListPageComponent} from "./workspace/workspace-list-page.component";
import {WorkspaceViewPageComponent} from "./workspace/workspace-view-page.component";
import {WorkspaceEditPageComponent} from "./workspace/workspace-edit-page.component";
import {WorkspaceEditWorkspaceComponent} from "./workspace/workspace-edit-workspace.component";
import {
  WorkspaceGridsterItemEditComponent,
  WorkspaceGridsterItemViewComponent
} from "./workspace/workspace-gridster-item.component";
import {OrderListPageComponent} from "./order/order-list-page.component";
import {OrderEditPageComponent} from "./order/order-edit-page.component";
import {OrderInformationPageComponent} from "./order/order-information-page.component";
import {OrderItemPageComponent} from "./order/order-item-page.component";
import {OrderContextActionsComponent} from "./order/order-context-actions.component";
import {OrderAnalysisPageComponent} from "./order/order-analysis-page.component";
import {OrderCopyModalComponent} from "./order/order-copy-modal.component";
import {SoloVisibilityInfoComponent} from "./common/directives/solo-visibility-info-component";
import {ReportScheduleModalService} from "./report-schedule/report-schedule-modal.service";
import {LineItemDiagnosticsPageComponent} from "./line-item/line-item-diagnostics-page.component";
import {LineItemReachPageComponent} from "./line-item/line-item-reach-page.component";
import {LineItemListPageComponent} from "./line-item/line-item-list-page.component";
import {LineItemCreativeListComponent} from "./line-item/line-item-creative-list.component";
import {LineItemTemplateListPageComponent} from "./line-item/line-item-template-list-page.component";
import {LineItemAnalysisPageComponent} from "./line-item/line-item-analysis-page.component";
import {LineItemContextActionsComponent} from "./line-item/line-item-context-actions.component";
import {LineItemCopyModalComponent} from "./line-item/line-item-copy-modal.component";
import {LineItemTemplateAnalysisPageComponent} from "./line-item/line-item-template-analysis-page.component";
import {LineItemTemplateContextActionsComponent} from "./line-item/line-item-template-context-actions.component";
import {LineItemListContextActionsComponent} from "./line-item/line-item-list-context-actions.component";
import {LineItemTemplateCopyModalComponent} from "./line-item/line-item-template-copy-modal.component";
import {LineItemEditPageComponent} from "./line-item/line-item-edit-page.component";
import {LineItemBulkActionModalComponent} from "./line-item/line-item-bulk-action-modal.component";
import {LineItemCpmFeesModalComponent} from "./line-item/line-item-cpm-fees-modal.component";
import {CreativeFromLineItemCopyModalComponent} from "./line-item/creative-from-line-item-copy-modal.component";
import {MpProductModalComponent} from "./line-item/mp-product-modal.component";
import {LineItemRetailProductModalComponent} from "./line-item/line-item-retail-product-modal.component";
import {OrderBudgetModalComponent} from "./order/order-budget-modal.component";
import {BudgetViewComponent} from "./order/budget-view.component";
import {OrderQuickLineItemFormComponent} from "./order/order-quick-line-item-form.component";
import {CreativeBulkActionModalComponent} from "./creative/creative-bulk-action-modal.component";
import {CreativeAttachModalComponent} from "./creative/creative-attach-modal.component";
import {CreativeCopyInBulkModalComponent} from "./creative/creative-copy-in-bulk-modal.component";
import {PrintLineItemListPageComponent} from "./print-line-item/print-line-item-list-page.component";
import {PrintLineItemEditPageComponent} from "./print-line-item/print-line-item-edit-page.component";
import {GetCampaignQrCodeModalComponent} from "./print-line-item/get-campaign-qr-code-modal.component";
import {SignInModule} from "./sign-in/sign-in.module";
import {WelcomePageComponent} from "./home/welcome-page.component";
import {WelcomeDataPageComponent} from "./home/welcome-data-page.component";
import {WelcomePoliticalPageComponent} from "./home/welcome-political-page.component";
import {ErrorsModule} from "./errors/errors.module";
import {ResourceSidebarActionsComponent} from "./base/resource-sidebar-actions.component";
import { SignInAuthListenerService } from './sign-in/sign-in-auth-listener.service';
import { AppRuntimeLifecycleService } from './base/app-runtime-lifecycle.service';
import {CampaignListPageComponent} from "./campaign/campaign-list-page.component";
import {CampaignEditPageComponent} from "./campaign/campaign-edit-page.component";
import {CampaignAnalysisPageComponent} from "./campaign/campaign-analysis-page.component";
import {CampaignContextActionsComponent} from "./campaign/campaign-context-actions.component";
import {AxLineItemListPageComponent} from "./campaign/ax-line-item-list-page.component";
import {LibraryCreativeListPageComponent} from "./library-creative/library-creative-list-page.component";
import {LibraryCreativeEditPageComponent} from "./library-creative/library-creative-edit-page.component";
import {LibraryCreativeContextActionsComponent} from "./library-creative/library-creative-context-actions.component";
import {LibraryCreativeListContextComponent} from "./library-creative/library-creative-list-context.component";
import {LibraryCreativeCopyModalComponent} from "./library-creative/library-creative-copy-modal.component";
import {LibraryCreativeLinkCreativeModalComponent} from "./library-creative/library-creative-link-creative-modal.component";
import {CreativeListPageComponent} from "./creative/creative-list-page.component";
import {CreativeEditPageComponent} from "./creative/creative-edit-page.component";
import {CreativeContextActionsComponent} from "./creative/creative-context-actions.component";
import {CreativeBulkUploadPageComponent} from "./creative/creative-bulk-upload-page.component";
import {CreativeCopyModalComponent} from "./creative/creative-copy-modal.component";
import {CreativeLinkLibraryModalComponent} from "./creative/creative-link-library-modal.component";
import {CreativeRetailProductModalComponent} from "./creative/creative-retail-product-modal.component";
import {RetailProductListPageComponent} from "./retail-product/retail-product-list-page.component";
import {RetailProductCreativesModalComponent} from "./retail-product/retail-product-creatives-modal.component";
import {RetailProductConversionTrackingModalComponent} from "./retail-product/retail-product-conversion-tracking-modal.component";
import {ReviewListPageComponent} from "./review/review-list-page.component";
import {AxReviewListPageComponent} from "./review/ax-review-list-page.component";
import {ReviewRejectModalComponent} from "./review/review-reject-modal.component";
import {ContextServiceConfigurationListPageComponent} from "./admin/context-service-configuration/context-service-configuration-list-page.component";
import {ContextServiceConfigurationEditPageComponent} from "./admin/context-service-configuration/context-service-configuration-edit-page.component";
import {UserListPageComponent} from "./admin/user/user-list-page.component";
import {UserEditPageComponent} from "./admin/user/user-edit-page.component";
import {UserEditRolesSectionComponent} from "./admin/user/user-edit-roles-section.component";
import {UserCopyModalComponent} from "./admin/user/user-copy-modal.component";
import {TwoFactorAuthRecoveryModalComponent} from "./admin/user/two-factor-auth-recovery-modal.component";
import {LayoutExampleEditPageComponent} from "./admin/layout-example/layout-example-edit-page.component";
import {LayoutExampleListPageComponent} from "./admin/layout-example/layout-example-list-page.component";
import {LayoutExampleRoutePageComponent} from "./admin/layout-example/layout-example-route-page.component";
import {LayoutExampleContextActionsComponent} from "./admin/layout-example/layout-example-context-actions.component";
import {LayoutExampleToLayoutCopyModalComponent} from "./admin/layout-example/layout-example-to-layout-copy-modal.component";
import {LayoutContextRoutePageComponent} from "./admin/layout/layout-context-route-page.component";
import {LayoutEditPageComponent} from "./admin/layout/layout-edit-page.component";
import {LayoutCopyModalComponent} from "./admin/layout/layout-copy-modal.component";
import {LayoutExampleCopyModalComponent} from "./admin/layout/layout-example-copy-modal.component";
import {LayoutListPageComponent} from "./admin/layout/layout-list-page.component";
import {LayoutRoutePageComponent} from "./admin/layout/layout-route-page.component";
import {ReportTemplateEditPageComponent} from "./report-template/report-template-edit-page.component";
import {ReportTemplateListPageComponent} from "./report-template/report-template-list-page.component";
import {ReportTemplateRoutePageComponent} from "./report-template/report-template-route-page.component";
import {ReportTemplateEditWorkspaceComponent} from "./report-template/report-template-edit-workspace.component";
import {ReportTemplateGridsterItemComponent} from "./report-template/report-template-gridster-item.component";
import {ReportTemplateWidgetModalComponent} from "./report-template/report-template-widget-modal.component";
import {ReportTemplateCopyModalComponent} from "./report-template/report-template-copy-modal.component";
import {ReportTemplateContextRoutePageComponent} from "./report-template/report-template-context-route-page.component";
import {NotificationPreferencesEditPageComponent} from "./admin/notification-preferences/notification-preferences-edit-page.component";
import {NetworkCdnTrafficComponent} from "./admin/network/network-cdn-traffic.component";
import {NetworkPageComponent} from "./admin/network/network-page.component";
import {UserProfilePageComponent} from "./admin/user-profile/user-profile-page.component";
import {ChangePasswordModalComponent} from "./admin/user-profile/change-password-modal.component";
import {CreatePasskeyModalComponent} from "./admin/user-profile/create-passkey-modal.component";
import {ReferenceDataUploadComponent} from "./admin/reference-data/reference-data-upload.component";
import {ReferenceDataPageComponent} from "./admin/reference-data/reference-data-page.component";
import {ExternalDemandSourceListPageComponent} from "./admin/external-demand-source/external-demand-source-list-page.component";
import {ExternalDemandSourceEditPageComponent} from "./admin/external-demand-source/external-demand-source-edit-page.component";
import {ExternalDemandSourceAnalysisPageComponent} from "./admin/external-demand-source/external-demand-source-analysis-page.component";
import {ExternalDemandSourceItemPageComponent} from "./admin/external-demand-source/external-demand-source-item-page.component";
import {ExternalDemandSourceRoutePageComponent} from "./admin/external-demand-source/external-demand-source-route-page.component";
import {ExternalAdUnitRoutePageComponent} from "./external-ad-unit/external-ad-unit-route-page.component";
import {ExternalAdUnitListPageComponent} from "./external-ad-unit/external-ad-unit-list-page.component";
import {ExternalAdUnitEditPageComponent} from "./external-ad-unit/external-ad-unit-edit-page.component";
import {ExternalAdUnitEditUsageComponent} from "./external-ad-unit/external-ad-unit-edit-usage.component";
import {ExternalAdUnitAnalysisPageComponent} from "./external-ad-unit/external-ad-unit-analysis-page.component";
import {ExternalAdUnitCopyModalComponent} from "./external-ad-unit/external-ad-unit-copy-modal.component";
import {AdUnitAnalysisPageComponent} from "./ad-unit/ad-unit-analysis-page.component";
import {AdUnitCreativeTotalsPreviewModalComponent} from "./ad-unit/ad-unit-creative-totals-preview-modal.component";
import {AdUnitEditPageComponent} from "./ad-unit/ad-unit-edit-page.component";
import {AdUnitListPageComponent} from "./ad-unit/ad-unit-list-page.component";
import {AdUnitCopyModalComponent} from "./ad-unit/ad-unit-copy-modal.component";
import {AdUnitSizeModalComponent} from "./ad-unit/ad-unit-size-modal.component";
import {AdUnitBulkActionModalComponent} from "./ad-unit/ad-unit-bulk-action-modal.component";
import {SiteEditPageComponent} from "./site/site-edit-page.component";
import {SiteAnalysisPageComponent} from "./site/site-analysis-page.component";
import {SiteListPageComponent} from "./site/site-list-page.component";
import {SiteCopyModalComponent} from "./site/site-copy-modal.component";
import {SiteBulkActionModalComponent} from "./site/site-bulk-action-modal.component";
import {EarningsAccountEditPageComponent} from "./earnings-account/earnings-account-edit-page.component";
import {EarningsAccountAnalysisPageComponent} from "./earnings-account/earnings-account-analysis-page.component";
import {EarningsAccountListPageComponent} from "./earnings-account/earnings-account-list-page.component";
import {EarningsAccountCopyModalComponent} from "./earnings-account/earnings-account-copy-modal.component";
import {TeamListPageComponent} from "./admin/team/team-list-page.component";
import {TeamEditPageComponent} from "./admin/team/team-edit-page.component";
import {TeamEditFeesComponent} from "./admin/team/team-edit-fees.component";
import {TeamAnalysisPageComponent} from "./admin/team/team-analysis-page.component";
import {TeamCopyModalComponent} from "./admin/team/team-copy-modal.component";
import {TeamObjectMembershipModalComponent} from "./admin/team/team-object-membership-modal.component";
import {TeamOwnershipModalComponent} from "./admin/team/team-ownership-modal.component";
import {SiteApprovalPreviewModalComponent} from "./site/site-approval-preview-modal.component";
import {SiteAccessModalComponent} from "./earnings-account/site-access-modal.component";
import { NgSelectModule } from '@ng-select/ng-select';
import {GridsterWorkspaceComponent} from "./common/directives/gridster-workspace.component";
import { Gridster, GridsterItem } from 'angular-gridster2';
import {TeamFeeComponent} from "./admin/team/team-fee-component";
import {MasqBannerComponent} from "./common/directives/banners/masq-banner-component";
import {NetworksTabComponent} from "./base/tabs/networks-tab.component";
import {NotificationsTabComponent} from "./base/tabs/notifications-tab.component";
import {LastVerbedTabComponent} from "./base/tabs/last-verbed-tab.component";
import {DefaultItemTitleComponent} from "./base/default-item-title.component";
import {GlobalSearchComponent} from "./search/global-search.component";
import {SearchPageComponent} from "./search/search-page.component";
import {BookingCalendarPageComponent} from "./booking-calendar/booking-calendar-page.component";
import {ReachAnalysisPageComponent} from "./reach-analysis/reach-analysis-page.component";
import {LabelRepeaterComponent} from "./common/directives/html/label-repeater.component";
import {LineItemListFiltersComponent} from "./line-item/line-item-list-filters.component";
import {OrderListFiltersComponent} from "./order/order-list-filters.component";
import {CreativeListFiltersComponent} from "./creative/creative-list-filters.component";
import {RetailProductListFiltersComponent} from "./retail-product/retail-product-list-filters.component";
import {ProductEditPageComponent} from "./admin/product/product-edit-page.component";
import {ProductListPageComponent} from "./admin/product/product-list-page.component";
import {ProductRoutePageComponent} from "./admin/product/product-route-page.component";
import {LayoutSelectorModalComponent} from "./admin/product/layout-selector-modal.component";
import {ProductCopyModalComponent} from "./admin/product/product-copy-modal.component";
import {MarketplaceProductCopyModalComponent} from "./admin/marketplace-product/marketplace-product-copy-modal.component";
import {MarketplaceProductEditPageComponent} from "./admin/marketplace-product/marketplace-product-edit-page.component";
import {MarketplaceProductListPageComponent} from "./admin/marketplace-product/marketplace-product-list-page.component";
import {MarketplaceProductRoutePageComponent} from "./admin/marketplace-product/marketplace-product-route-page.component";
import {ReleaseNoteCardComponent} from "./base/simple/release-note-card.component";
import {ReleaseNoteAssetUploadComponent} from "./base/simple/release-note-asset-upload.component";
import {ReleaseNotesListPageComponent} from "./base/simple/release-notes-list-page.component";
import {ReleaseNotesEditPageComponent} from "./base/simple/release-notes-edit-page.component";
import {ReleaseNotesDisplayComponent} from "./base/simple/release-notes-display.component";
import {ReleaseNotesModalComponent} from "./base/simple/release-notes-modal.component";
import {ReleaseNotesModalService} from "./base/simple/release-notes-modal.service";
import {ConfirmNavigationModalComponent} from "./common/modal/confirm-navigation-modal.component";
import {AngularErrorHandlerService} from "./errors/angular-error-handler.service";
import {ReleaseNotesHelper} from "../components/util/release-notes-helper.service";
import {ContentSummaryHelper} from "../components/util/content-summary-helper.service";
import {CreativeHelper} from "./creative/creative-helper.service";
import {ChartOptionsUtil} from "./common/options/chart-options-util.service";
import {AdnInactiveTargetsService} from "../components/util/adn-inactive-targets.service";
import {SupportingPagesPageComponent} from "./base/simple/supporting-pages-page.component";
import {SupportPageComponent} from "./base/simple/support-page.component";
import {TestStylePageComponent} from "./base/simple/test-style-page.component";
import {TcfPageComponent} from "./base/simple/tcf-page.component";
import {TfaRecoveryPageComponent} from "./base/simple/tfa-recovery-page.component";
import {ElectionsListPageComponent} from "./base/simple/elections-list-page.component";
import {ScanResultsPageComponent} from "./base/simple/scan-results-page.component";
import {VersionHistoryPageComponent} from "./base/simple/version-history-page.component";
import {VersionCopyPageComponent} from "./base/simple/version-copy-page.component";
import {CreativeCheckerPageComponent} from "./base/simple/creative-checker-page.component";
import {AdDensityPageComponent} from "./base/simple/ad-density-page.component";
import {ShotterPageComponent} from "./base/simple/shotter-page.component";
import {PreviewShotterPageComponent} from "./base/simple/preview-shotter-page.component";
import {TransparencyShotterPageComponent} from "./base/simple/transparency-shotter-page.component";
import {AdTransparencyPreviewPageComponent} from "./base/simple/ad-transparency-preview-page.component";
import {PoliticalOverlayPreviewPageComponent} from "./base/simple/political-overlay-preview-page.component";
import {PoliticalPreviewPageComponent} from "./base/simple/political-preview-page.component";
import {DummyPageComponent} from "./base/nude/dummy-page.component";
import {ReleaseNotesPageComponent} from "./base/nude/release-notes-page.component";
import {AdnDocsPageComponent} from "./base/nude/adn-docs-page.component";
import {NudePreviewPageComponent} from "./base/nude/nude-preview-page.component";
import {UpdateAdvertiserPageComponent} from "./base/nude/update-advertiser-page.component";
import {PoliticalEntityCampaignsPanelComponent} from "./political-entity/political-entity-campaigns-panel.component";
import {PoliticalEntityEditPageComponent} from "./political-entity/political-entity-edit-page.component";
import {PoliticalEntityListPageComponent} from "./political-entity/political-entity-list-page.component";
import {PoliticalEntityRoutePageComponent} from "./political-entity/political-entity-route-page.component";
import {PoliticalCampaignEditPageComponent} from "./political-campaign/political-campaign-edit-page.component";
import {PoliticalCampaignListPageComponent} from "./political-campaign/political-campaign-list-page.component";
import {PoliticalCampaignRoutePageComponent} from "./political-campaign/political-campaign-route-page.component";
import {StructurePageComponent} from "./base/simple/structure-page.component";
import {ChainCountsPageComponent} from "./base/simple/chain-counts-page.component";
import {PoliticalSponsorsPageComponent} from "./base/simple/political-sponsors-page.component";
import {FieldsModule} from "./fields/fields.module";
import {MappingRoutePageComponent} from "./mapping/mapping-route-page.component";
import {QueryRoutePageComponent} from "./report/query/query-route-page.component";
import {SegmentRoutePageComponent} from "./segment/segment-route-page.component";
import {SegmentListPageComponent} from "./segment/segment-list-page.component";
import {SegmentItemPageComponent} from "./segment/segment-item-page.component";
import {SegmentEditPageComponent} from "./segment/segment-edit-page.component";
import {SegmentSidePanelComponent} from "./segment/segment-side-panel.component";
import {SegmentTriggerModalComponent} from "./segment/segment-trigger-modal.component";
import {TriggerFormComponent} from "./trigger/trigger-form.component";
import {TriggerRoutePageComponent} from "./trigger/trigger-route-page.component";
import {TriggerListPageComponent} from "./trigger/trigger-list-page.component";
import {TriggerItemPageComponent} from "./trigger/trigger-item-page.component";
import {TriggerEditPageComponent} from "./trigger/trigger-edit-page.component";
import {TriggerContextRoutePageComponent} from "./trigger/trigger-context-route-page.component";
import {TriggerCopyModalComponent} from "./trigger/trigger-copy-modal.component";
import {SegmentContextRoutePageComponent} from "./segment/segment-context-route-page.component";
import {SegmentCopyModalComponent} from "./segment/segment-copy-modal.component";
import {ThirdPartyAudienceEditRoutePageComponent} from "./third-party-audience/third-party-audience-edit-route-page.component";
import {ThirdPartyAudienceRoutePageComponent} from "./third-party-audience/third-party-audience-route-page.component";
import {ThirdPartyAudienceListPageComponent} from "./third-party-audience/third-party-audience-list-page.component";
import {ThirdPartyAudienceItemPageComponent} from "./third-party-audience/third-party-audience-item-page.component";
import {ThirdPartyAudienceEditPageComponent} from "./third-party-audience/third-party-audience-edit-page.component";
import {ThirdPartyAudienceUploadModalComponent} from "./third-party-audience/third-party-audience-upload-modal.component";
import {ThirdPartyAudiencePostcodeModalComponent} from "./third-party-audience/third-party-audience-postcode-modal.component";
import {BaseShellLayoutComponent} from "./base/base-shell-layout.component";
import {NudeShellLayoutComponent} from "./base/nude/nude-shell-layout.component";
import {
  RouteTwoPanelBottomLayoutComponent,
  RouteTwoPanelLayoutComponent
} from "./common/directives/route-two-panel-layout.component";
import {DefaultItemTitleRouteComponent} from "./base/default-item-title-route.component";
import {StateTitleComponent} from "./common/directives/html/state-title.component";
import {
  ContainerStateTitleComponent,
  EmptyRouteViewComponent,
  StaticRouteTitleComponent
} from "./common/directives/html/route-view-components";
import {ReleaseNotesEditTitleComponent} from "./base/simple/release-notes-edit-title.component";
import {DocumentShellService} from "./base/document-shell.service";
import {AppComponent} from "./app.component";
import {registerFeatureStates} from './routing/register-feature-states';

import "ngx-toastr/toastr";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import './common/options/highcharts-defaults';
import '../style/object-diff.scss';
import '../style/app.scss';

const namedResourceProviders = [
  adUnitResourceProvider,
  adUnitTagResourceProvider,
  adnExecutionResourceProvider,
  advertiserNoAuthResourceProvider,
  advertiserResourceProvider,
  apiKeyResourceProvider,
  blocklistResourceProvider,
  cdnAssetResourceProvider,
  consentStatisticResourceProvider,
  contextServiceConfigurationResourceProvider,
  couponResourceProvider,
  creativePreviewResourceProvider,
  creativeResourceProvider,
  creativeSetInfoResourceProvider,
  creativeSetResourceProvider,
  customEventResourceProvider,
  dataExportResourceProvider,
  diagnosticRequestExecutionResourceProvider,
  earningsAccountResourceProvider,
  electionNoAuthResourceProvider,
  electionResourceProvider,
  emailTemplateResourceProvider,
  emailTranslationResourceProvider,
  emailTranslationTestResourceProvider,
  externalAdUnitResourceProvider,
  externalDemandSourceResourceProvider,
  fieldMappingResourceProvider,
  folderResourceProvider,
  folioResourceProvider,
  invoiceTranslationPreviewResourceProvider,
  invoiceTranslationResourceProvider,
  layoutIncludeResourceProvider,
  layoutResourceProvider,
  libraryCreativeResourceProvider,
  lineItemBookingsResourceProvider,
  lineItemResourceProvider,
  lineItemReviewResourceProvider,
  lineItemTemplateResourceProvider,
  marketplaceProductOptionsResourceProvider,
  marketplaceProductResourceProvider,
  networkDataResourceProvider,
  networkForecastResourceProvider,
  networkInfoResourceProvider,
  networkProfileResourceProvider,
  networkResourceProvider,
  noteResourceProvider,
  notificationPreferencesResourceProvider,
  notificationResourceProvider,
  orderResourceProvider,
  paymentResourceProvider,
  politicalAdsResourceProvider,
  politicalCampaignResourceProvider,
  politicalEntityResourceProvider,
  politicalSponsorResourceProvider,
  previewResourceProvider,
  printLineItemResourceProvider,
  productFeedFacetsProvider,
  productFeedResourceProvider,
  productFeedSearchProvider,
  productResourceProvider,
  publicPoliticalCampaignResourceProvider,
  publisherInfoResourceProvider,
  reportComponentResourceProvider,
  reportExecutionResourceProvider,
  reportScheduleResourceProvider,
  reportTemplateResourceProvider,
  reportTranslationResourceProvider,
  roleResourceProvider,
  rulesetResourceProvider,
  segmentResourceProvider,
  siteCountryResourceProvider,
  siteGroupResourceProvider,
  siteResourceProvider,
  targetingTemplateResourceProvider,
  teamGroupResourceProvider,
  teamResourceProvider,
  thirdPartyAudienceResourceProvider,
  triggerResourceProvider,
  userProfileResourceProvider,
  userResourceProvider,
  workspaceResourceProvider
].map((provider) => ({
  provide: provider.provide.name.replace(/Resource$/, ''),
  useExisting: provider.provide
}));
namedResourceProviders.push({
  provide: 'CdnUpload',
  useExisting: cdnAssetResourceProvider.provide
});

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      timeOut: 10000,
      preventDuplicates: true,
      enableHtml: true
    }),
    HttpClientModule,
    UIRouterModule.forRoot({
      states: [],
      useHash: false,
      deferIntercept: true
    }),
    TranslateModule.forRoot({
      parser: {provide: TranslateParser, useClass: AdnTranslateParser}
    }),
    FormsModule,
    QuillModule.forRoot(),
    PopoverModule,
    ButtonsModule,
    CollapseModule,
    ModalModule,
    BsDatepickerModule,
    TimepickerModule,
    TabsModule,
    BsDropdownModule,
    ResourcesModule,
    AppSharedModule,
    AdnSelectModule,
    FormControlsModule,
    AdminListModule,
    CountrySelectModule,
    ChartsModule,
    StatsTablesModule,
    ListColumnSelectorsModule,
    AdminResourcePagesModule,
    SearchSelectModule,
    DeliveryWidgetsModule,
    CreativePreviewModule,
    CreativeAssetsModule,
    CustomEventModule,
    ApiKeyModule,
    RulesetModule,
    ExplorationModule,
    PublisherStatsModule,
    CdnUploadModule,
    RoleModule,
    EmailTemplateModule,
    EmailTranslationModule,
    InvoiceTranslationModule,
    ReportTranslationModule,
    TierModule,
    LayoutIncludeModule,
    SiteGroupModule,
    TeamGroupModule,
    DataExportModule,
    ReportScheduleModule,
    ReportWidgetsModule,
    TargetingModule,
    LineItemLabelsModule,
    WorkspaceWidgetsModule,
    SignInModule,
    ErrorsModule,
    FieldsModule,
    PaymentModule,
    CouponModule,
    BlocklistModule,
    NotesModule,
    ObjectStatesModule,
    ResourceStatusModule,
    CodemirrorModule,
    NgSelectModule,
    Gridster,
    GridsterItem
  ],
  declarations: [
    AppComponent,
    BaseShellLayoutComponent,
    NudeShellLayoutComponent,
    RouteTwoPanelLayoutComponent,
    RouteTwoPanelBottomLayoutComponent,
    DefaultItemTitleRouteComponent,
    StateTitleComponent,
    ContainerStateTitleComponent,
    EmptyRouteViewComponent,
    StaticRouteTitleComponent,
    ReleaseNotesEditTitleComponent,
    AdvertiserListPageComponent,
    AdvertiserEditPageComponent,
    AdvertiserInviteEditorModalComponent,
    AdvertiserItemPageComponent,
    AdvertiserInformationPageComponent,
    AdvertiserAnalysisPageComponent,
    CreativeUserStatesComponent,
    LayoutComponentSummaryComponent,
    LineItemDiagnosticsPageComponent,
    LineItemReachPageComponent,
    LineItemListPageComponent,
    LineItemCreativeListComponent,
    LineItemTemplateListPageComponent,
    LineItemAnalysisPageComponent,
    LineItemContextActionsComponent,
    LineItemCopyModalComponent,
    LineItemTemplateAnalysisPageComponent,
    LineItemTemplateContextActionsComponent,
    LineItemListContextActionsComponent,
    LineItemTemplateCopyModalComponent,
    LineItemEditPageComponent,
    LineItemBulkActionModalComponent,
    LineItemCpmFeesModalComponent,
    CreativeFromLineItemCopyModalComponent,
    MpProductModalComponent,
    LineItemRetailProductModalComponent,
    OrderBudgetModalComponent,
    BudgetViewComponent,
    OrderQuickLineItemFormComponent,
    CreativeBulkActionModalComponent,
    CreativeAttachModalComponent,
    CreativeCopyInBulkModalComponent,
    PrintLineItemListPageComponent,
    PrintLineItemEditPageComponent,
    GetCampaignQrCodeModalComponent,
    CampaignListPageComponent,
    CampaignEditPageComponent,
    CampaignAnalysisPageComponent,
    CampaignContextActionsComponent,
    AxLineItemListPageComponent,
    LibraryCreativeListPageComponent,
    LibraryCreativeEditPageComponent,
    LibraryCreativeContextActionsComponent,
    LibraryCreativeListContextComponent,
    LibraryCreativeCopyModalComponent,
    LibraryCreativeLinkCreativeModalComponent,
    CreativeListPageComponent,
    CreativeEditPageComponent,
    CreativeContextActionsComponent,
    CreativeBulkUploadPageComponent,
    CreativeCopyModalComponent,
    CreativeLinkLibraryModalComponent,
    CreativeRetailProductModalComponent,
    RetailProductListPageComponent,
    RetailProductCreativesModalComponent,
    RetailProductConversionTrackingModalComponent,
    ReviewListPageComponent,
    AxReviewListPageComponent,
    ReviewRejectModalComponent,
    ContextServiceConfigurationListPageComponent,
    ContextServiceConfigurationEditPageComponent,
    UserListPageComponent,
    UserEditPageComponent,
    UserEditRolesSectionComponent,
    UserCopyModalComponent,
    TwoFactorAuthRecoveryModalComponent,
    AdTagGeneratorPageComponent,
    AdTagComponent,
    EntityDiagramComponent,
    FolderEditPageComponent,
    FolderAnalysisPageComponent,
    FolderCopyModalComponent,
    FolderContextRoutePageComponent,
    FolderItemPageComponent,
    FolderListPageComponent,
    FolderRoutePageComponent,
    CreativeSetRoutePageComponent,
    CreativeSetListPageComponent,
    CreativeSetItemPageComponent,
    CreativeSetEditPageComponent,
    CreativeSetAnalysisPageComponent,
    CreativeSetContextRoutePageComponent,
    CreativeSetContextActionsComponent,
    CreativeSetCopyModalComponent,
    CreativeSetBulkActionModalComponent,
    FolioRoutePageComponent,
    FolioListPageComponent,
    FolioItemPageComponent,
    FolioEditPageComponent,
    FolioInformationPageComponent,
    FolioContextRoutePageComponent,
    FolioContextActionsComponent,
    FolioListObjectStatesComponent,
    FolioCopyModalComponent,
    WorkspaceRoutePageComponent,
    WorkspaceListPageComponent,
    WorkspaceViewPageComponent,
    WorkspaceEditPageComponent,
    WorkspaceEditWorkspaceComponent,
    WorkspaceGridsterItemViewComponent,
    WorkspaceGridsterItemEditComponent,
    OrderListPageComponent,
    OrderEditPageComponent,
    OrderItemPageComponent,
    OrderContextActionsComponent,
    OrderInformationPageComponent,
    OrderAnalysisPageComponent,
    OrderCopyModalComponent,
    SoloVisibilityInfoComponent,
    LayoutExampleEditPageComponent,
    LayoutExampleListPageComponent,
    LayoutExampleRoutePageComponent,
    LayoutExampleContextActionsComponent,
    LayoutExampleToLayoutCopyModalComponent,
    LayoutContextRoutePageComponent,
    LayoutEditPageComponent,
    LayoutCopyModalComponent,
    LayoutExampleCopyModalComponent,
    LayoutListPageComponent,
    LayoutRoutePageComponent,
    ReportTemplateEditPageComponent,
    ReportTemplateListPageComponent,
    ReportTemplateRoutePageComponent,
    ReportTemplateEditWorkspaceComponent,
    ReportTemplateGridsterItemComponent,
    ReportTemplateWidgetModalComponent,
    ReportTemplateCopyModalComponent,
    ReportTemplateContextRoutePageComponent,
    NotificationPreferencesEditPageComponent,
    NetworkCdnTrafficComponent,
    NetworkPageComponent,
    UserProfilePageComponent,
    ChangePasswordModalComponent,
    CreatePasskeyModalComponent,
    WelcomePageComponent,
    WelcomeDataPageComponent,
    WelcomePoliticalPageComponent,
    ResourceSidebarActionsComponent,
    ReferenceDataUploadComponent,
    ReferenceDataPageComponent,
    ExternalDemandSourceListPageComponent,
    ExternalDemandSourceEditPageComponent,
    ExternalDemandSourceAnalysisPageComponent,
    ExternalDemandSourceItemPageComponent,
    ExternalDemandSourceRoutePageComponent,
    ExternalAdUnitRoutePageComponent,
    ExternalAdUnitListPageComponent,
    ExternalAdUnitEditPageComponent,
    ExternalAdUnitEditUsageComponent,
    ExternalAdUnitAnalysisPageComponent,
    ExternalAdUnitCopyModalComponent,
    AdUnitAnalysisPageComponent,
    AdUnitCreativeTotalsPreviewModalComponent,
    AdUnitEditPageComponent,
    AdUnitListPageComponent,
    AdUnitCopyModalComponent,
    AdUnitSizeModalComponent,
    AdUnitBulkActionModalComponent,
    SiteEditPageComponent,
    SiteAnalysisPageComponent,
    SiteListPageComponent,
    SiteCopyModalComponent,
    SiteBulkActionModalComponent,
    EarningsAccountEditPageComponent,
    EarningsAccountAnalysisPageComponent,
    EarningsAccountListPageComponent,
    EarningsAccountCopyModalComponent,
    TeamListPageComponent,
    TeamEditPageComponent,
    TeamEditFeesComponent,
    TeamAnalysisPageComponent,
    TeamCopyModalComponent,
    TeamObjectMembershipModalComponent,
    TeamOwnershipModalComponent,
    SiteApprovalPreviewModalComponent,
    SiteAccessModalComponent,
    CollapsibleSidebarComponent,
    SessionInfoComponent,
    BaseNewButtonComponent,
    GridsterWorkspaceComponent,
    TeamFeeComponent,
    MasqBannerComponent,
    NetworksTabComponent,
    NotificationsTabComponent,
    LastVerbedTabComponent,
    DefaultItemTitleComponent,
    GlobalSearchComponent,
    SearchPageComponent,
    BookingCalendarPageComponent,
    ReachAnalysisPageComponent,
    LabelRepeaterComponent,
    LineItemListFiltersComponent,
    OrderListFiltersComponent,
    CreativeListFiltersComponent,
    RetailProductListFiltersComponent,
    QueryRoutePageComponent,
    ReportRoutePageComponent,
    ReportListPageComponent,
    ProductEditPageComponent,
    ProductListPageComponent,
    ProductRoutePageComponent,
    LayoutSelectorModalComponent,
    ProductCopyModalComponent,
    MarketplaceProductCopyModalComponent,
    MarketplaceProductEditPageComponent,
    MarketplaceProductListPageComponent,
    MarketplaceProductRoutePageComponent,
    ReleaseNoteCardComponent,
    ReleaseNoteAssetUploadComponent,
    ReleaseNotesListPageComponent,
    ReleaseNotesEditPageComponent,
    ReleaseNotesDisplayComponent,
    ReleaseNotesModalComponent,
    ConfirmNavigationModalComponent,
    SupportingPagesPageComponent,
    SupportPageComponent,
    TestStylePageComponent,
    TcfPageComponent,
    TfaRecoveryPageComponent,
    ElectionsListPageComponent,
    ScanResultsPageComponent,
    VersionHistoryPageComponent,
    VersionCopyPageComponent,
    CreativeCheckerPageComponent,
    AdDensityPageComponent,
    ShotterPageComponent,
    PreviewShotterPageComponent,
    TransparencyShotterPageComponent,
    AdTransparencyPreviewPageComponent,
    PoliticalOverlayPreviewPageComponent,
    PoliticalPreviewPageComponent,
    DummyPageComponent,
    ReleaseNotesPageComponent,
    AdnDocsPageComponent,
    NudePreviewPageComponent,
    UpdateAdvertiserPageComponent,
    PoliticalEntityCampaignsPanelComponent,
    PoliticalEntityEditPageComponent,
    PoliticalEntityListPageComponent,
    PoliticalEntityRoutePageComponent,
    PoliticalCampaignEditPageComponent,
    PoliticalCampaignListPageComponent,
    PoliticalCampaignRoutePageComponent,
    StructurePageComponent,
    ChainCountsPageComponent,
    PoliticalSponsorsPageComponent,
    MappingRoutePageComponent,
    SegmentRoutePageComponent,
    SegmentListPageComponent,
    SegmentItemPageComponent,
    SegmentEditPageComponent,
    SegmentSidePanelComponent,
    SegmentTriggerModalComponent,
    TriggerFormComponent,
    TriggerRoutePageComponent,
    TriggerListPageComponent,
    TriggerItemPageComponent,
    TriggerEditPageComponent,
    TriggerContextRoutePageComponent,
    TriggerCopyModalComponent,
    SegmentContextRoutePageComponent,
    SegmentCopyModalComponent,
    ThirdPartyAudienceEditRoutePageComponent,
    ThirdPartyAudienceRoutePageComponent,
    ThirdPartyAudienceListPageComponent,
    ThirdPartyAudienceItemPageComponent,
    ThirdPartyAudienceEditPageComponent,
    ThirdPartyAudienceUploadModalComponent,
    ThirdPartyAudiencePostcodeModalComponent
  ],
  providers: [
    provideZonelessChangeDetection(),
    DocumentShellService,
    ReleaseNotesHelper,
    ContentSummaryHelper,
    CreativeHelper,
    ChartOptionsUtil,
    AdnInactiveTargetsService,
    ReleaseNotesModalService,
    ReportScheduleModalService,
    {
      provide: MODAL_CONFIG_DEFAULT_OVERRIDE,
      useValue: {keyboard: true}
    },
    localUserProfileTokenProvider,
    localNetworkProfileTokenProvider,
    {provide: ErrorHandler, useClass: AngularErrorHandlerService},
    adnStatFilterProvider,
    PercentPipe,
    DecimalPipe,
    CurrencyPipe,
    OgTranslatePipe,
    LocaleDatePipe,
    LocaleNumberPipe,
    LocalePercentPipe,
    SafeHtmlPipe,
    StatPipe,
    DisplayStatPipe,
    PercentagePipe,
    AdnNumberPipe,
    IsoCurrencyPipe,
    StringToCurrencyPipe,
    KeyValuePipe,
    GuardTypePipe,
    MimeTypeKeyPipe,
    adUnitResourceProvider,
    adUnitTagResourceProvider,
    adnExecutionResourceProvider,
    advertiserNoAuthResourceProvider,
    advertiserResourceProvider,
    apiKeyResourceProvider,
    blocklistResourceProvider,
    cdnAssetResourceProvider,
    consentStatisticResourceProvider,
    contextServiceConfigurationResourceProvider,
    couponResourceProvider,
    creativePreviewResourceProvider,
    creativeResourceProvider,
    creativeSetInfoResourceProvider,
    creativeSetResourceProvider,
    customEventResourceProvider,
    dataExportResourceProvider,
    diagnosticRequestExecutionResourceProvider,
    earningsAccountResourceProvider,
    electionNoAuthResourceProvider,
    electionResourceProvider,
    emailTemplateResourceProvider,
    emailTranslationResourceProvider,
    emailTranslationTestResourceProvider,
    externalAdUnitResourceProvider,
    externalDemandSourceResourceProvider,
    fieldMappingResourceProvider,
    folderResourceProvider,
    folioResourceProvider,
    invoiceTranslationPreviewResourceProvider,
    invoiceTranslationResourceProvider,
    layoutIncludeResourceProvider,
    layoutResourceProvider,
    libraryCreativeResourceProvider,
    lineItemBookingsResourceProvider,
    lineItemResourceProvider,
    lineItemReviewResourceProvider,
    lineItemTemplateResourceProvider,
    marketplaceProductOptionsResourceProvider,
    marketplaceProductResourceProvider,
    networkDataResourceProvider,
    networkForecastResourceProvider,
    networkInfoResourceProvider,
    networkProfileResourceProvider,
    networkResourceProvider,
    noteResourceProvider,
    notificationPreferencesResourceProvider,
    notificationResourceProvider,
    orderResourceProvider,
    paymentResourceProvider,
    politicalAdsResourceProvider,
    politicalCampaignResourceProvider,
    politicalEntityResourceProvider,
    politicalSponsorResourceProvider,
    previewResourceProvider,
    printLineItemResourceProvider,
    productFeedFacetsProvider,
    productFeedResourceProvider,
    productFeedSearchProvider,
    productResourceProvider,
    publicPoliticalCampaignResourceProvider,
    publisherInfoResourceProvider,
    reportComponentResourceProvider,
    reportExecutionResourceProvider,
    reportScheduleResourceProvider,
    reportTemplateResourceProvider,
    reportTranslationResourceProvider,
    roleResourceProvider,
    rulesetResourceProvider,
    segmentResourceProvider,
    siteCountryResourceProvider,
    siteGroupResourceProvider,
    siteResourceProvider,
    targetingTemplateResourceProvider,
    teamGroupResourceProvider,
    teamResourceProvider,
    thirdPartyAudienceResourceProvider,
    triggerResourceProvider,
    userProfileResourceProvider,
    userResourceProvider,
    workspaceResourceProvider,
    ...namedResourceProviders,
    ...angularPlatformProviders
  ]
})
export class AppModule {
  constructor(
    private readonly translate: TranslateService,
    private readonly injector: Injector
  ) {
  }

  ngDoBootstrap(appRef: ApplicationRef): void {
    const uiRouter = this.injector.get(UIRouter);
    installMissingTranslateKeyReporting(
      this.translate,
      this.injector.get(AdnLogService),
      () => ({
        href: typeof location !== 'undefined' ? location.href : undefined,
        stateName: uiRouter.globals?.current?.name
      })
    );
    this.translate.setDefaultLang("en");
    this.translate.setTranslation("en", ADN_TRANSLATIONS);
    registerFeatureStates(uiRouter, this.injector);
    this.injector.get(AppRuntimeLifecycleService).init();
    this.injector.get(SignInAuthListenerService);
    appRef.bootstrap(AppComponent);
    this.injector.get(DocumentShellService).init();
  }
}
